import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Inbox, ShoppingBag, FileText, Star, Film, HelpCircle, Package as PackageIcon, Gift, Plus, Trash2, Save, X, LayoutGrid, Heart } from "lucide-react";
import AdminCRM from "@/pages/admin/CRM";

const SECTIONS = [
  { key: "crm", label: "CRM Pipeline", icon: LayoutGrid },
  { key: "inquiries", label: "Inquiries", icon: Inbox },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "memorial_pages", label: "Memorial Pages", icon: Heart },
  { key: "blog_posts", label: "Blog", icon: FileText },
  { key: "testimonials", label: "Testimonials", icon: Star },
  { key: "films", label: "Films", icon: Film },
  { key: "faqs", label: "FAQs", icon: HelpCircle },
  { key: "packages", label: "Packages", icon: PackageIcon },
  { key: "voucher_amounts", label: "Voucher Amounts", icon: Gift },
];

const SCHEMAS = {
  blog_posts: [
    { field: "slug", type: "text" }, { field: "title", type: "text" }, { field: "excerpt", type: "text" },
    { field: "body", type: "textarea" }, { field: "cover_url", type: "text" }, { field: "author", type: "text" }, { field: "published", type: "checkbox" },
  ],
  testimonials: [{ field: "name", type: "text" }, { field: "relation", type: "text" }, { field: "quote", type: "textarea" }, { field: "rating", type: "number" }, { field: "photo_url", type: "text" }],
  films: [{ field: "title", type: "text" }, { field: "description", type: "textarea" }, { field: "video_url", type: "text" }, { field: "thumbnail_url", type: "text" }, { field: "category", type: "text" }],
  faqs: [{ field: "question", type: "text" }, { field: "answer", type: "textarea" }, { field: "order", type: "number" }],
  packages: [
    { field: "slug", type: "text" }, { field: "name", type: "text" }, { field: "tagline", type: "text" },
    { field: "price_aud", type: "number" }, { field: "features", type: "list" }, { field: "highlighted", type: "checkbox" }, { field: "order", type: "number" },
  ],
  voucher_amounts: [{ field: "label", type: "text" }, { field: "amount_aud", type: "number" }, { field: "order", type: "number" }],
  memorial_pages: [
    { field: "slug", type: "text" }, { field: "deceased_name", type: "text" }, { field: "subtitle", type: "text" },
    { field: "vimeo_url", type: "text" }, { field: "service_date_display", type: "text" },
    { field: "description", type: "textarea" }, { field: "google_review_url", type: "text" },
    { field: "cta_copy", type: "textarea" }, { field: "published", type: "checkbox" },
  ],
};

function EditRow({ item, schema, onSave, onCancel }) {
  const [form, setForm] = useState(() => {
    const f = { ...(item || {}) };
    schema.forEach((s) => {
      if (s.type === "list" && !Array.isArray(f[s.field])) f[s.field] = f[s.field] ? String(f[s.field]).split("\n") : [];
    });
    return f;
  });
  return (
    <div className="bg-white p-6 rounded-lg border border-primary/40 mb-4">
      <div className="grid md:grid-cols-2 gap-4">
        {schema.map((s) => (
          <div key={s.field} className={s.type === "textarea" || s.type === "list" ? "md:col-span-2" : ""}>
            <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">{s.field}</label>
            {s.type === "textarea" ? (
              <textarea className="input-field min-h-[100px]" value={form[s.field] || ""} onChange={(e) => setForm({ ...form, [s.field]: e.target.value })} />
            ) : s.type === "list" ? (
              <textarea className="input-field min-h-[120px]" placeholder="One item per line"
                value={(form[s.field] || []).join("\n")}
                onChange={(e) => setForm({ ...form, [s.field]: e.target.value.split("\n").filter(Boolean) })} />
            ) : s.type === "checkbox" ? (
              <input type="checkbox" checked={!!form[s.field]} onChange={(e) => setForm({ ...form, [s.field]: e.target.checked })} />
            ) : s.type === "number" ? (
              <input type="number" step="any" className="input-field" value={form[s.field] ?? ""} onChange={(e) => setForm({ ...form, [s.field]: e.target.value === "" ? "" : Number(e.target.value) })} />
            ) : (
              <input className="input-field" value={form[s.field] || ""} onChange={(e) => setForm({ ...form, [s.field]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <button type="button" className="btn-primary text-sm" onClick={() => onSave(form)} data-testid="admin-save"><Save size={14} /> Save</button>
        <button type="button" className="btn-outline text-sm" onClick={onCancel}><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("crm");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | item.id

  const load = async () => {
    if (section === "crm") return; // handled by AdminCRM
    try {
      const endpoint = section === "orders" ? "/admin/orders" : `/admin/${section}`;
      const { data } = await api.get(endpoint);
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { if (user && user !== false) load(); setEditing(null); /* eslint-disable-next-line */ }, [section, user]);

  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-warmgrey">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const isReadOnly = section === "inquiries" || section === "orders";
  const schema = SCHEMAS[section] || [];

  const save = async (form) => {
    try {
      if (editing === "new") await api.post(`/admin/${section}`, form);
      else await api.put(`/admin/${section}/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${section}/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-cream flex" data-testid="page-admin-dashboard">
      <aside className="w-64 bg-secondary text-white p-6 sticky top-0 h-screen">
        <div className="font-serif text-xl mb-1">Family Legacy</div>
        <div className="text-xs text-white/50 mb-8">{user?.email}</div>
        <nav className="space-y-1">
          {SECTIONS.map((s) => (
            <button key={s.key} onClick={() => setSection(s.key)}
              data-testid={`admin-nav-${s.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded text-sm transition ${section === s.key ? "bg-primary text-white" : "text-white/70 hover:bg-white/5"}`}>
              <s.icon size={16} /> {s.label}
            </button>
          ))}
        </nav>
        <button onClick={async () => { await logout(); nav("/admin/login"); }} className="mt-8 w-full flex items-center gap-3 px-3 py-2 rounded text-sm text-white/60 hover:text-white" data-testid="admin-logout">
          <LogOut size={16} /> Sign Out
        </button>
      </aside>

      <div className="flex-1 p-8 overflow-hidden flex flex-col">
        {section === "crm" ? (
          <AdminCRM />
        ) : (
          <>
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-serif text-3xl">{SECTIONS.find((s) => s.key === section)?.label}</h1>
              {!isReadOnly && editing !== "new" && (
                <button onClick={() => setEditing("new")} className="btn-primary text-sm" data-testid="admin-new">
                  <Plus size={16} /> New
                </button>
              )}
            </div>

            {editing === "new" && <EditRow item={null} schema={schema} onSave={save} onCancel={() => setEditing(null)} />}

        <div className="bg-white rounded-xl border border-border-warm overflow-hidden">
          {items.length === 0 ? (
            <div className="p-10 text-center text-warmgrey-muted" data-testid="admin-empty">No items yet.</div>
          ) : (
            <div className="divide-y divide-border-warm">
              {items.map((it) => (
                <div key={it.id}>
                  {editing === it.id ? (
                    <div className="p-4"><EditRow item={it} schema={schema} onSave={save} onCancel={() => setEditing(null)} /></div>
                  ) : (
                    <div className="p-5 flex items-start justify-between gap-4 hover:bg-cream/50">
                      <div className="min-w-0 flex-1">
                        {section === "inquiries" ? (
                          <>
                            <div className="flex items-center gap-3 mb-1">
                              <span className="font-medium text-headline">{it.name}</span>
                              <span className="text-xs text-warmgrey-muted">{it.email}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${it.inquiry_type === "funeral_urgent" ? "bg-red-100 text-red-700" : "bg-primary/10 text-primary"}`}>{it.inquiry_type}</span>
                            </div>
                            <p className="text-sm text-warmgrey line-clamp-2">{it.message}</p>
                            <p className="text-xs text-warmgrey-muted mt-2">{new Date(it.created_at).toLocaleString()}</p>
                          </>
                        ) : section === "orders" ? (
                          <>
                            <div className="flex items-center gap-3 mb-1">
                              <span className="font-medium text-headline">{it.product_name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${it.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-800"}`}>{it.payment_status}</span>
                            </div>
                            <p className="text-sm text-warmgrey">
                              AUD ${it.amount} — {it.order_type} — {it.metadata?.buyer_email}
                              {it.voucher_code && <span className="ml-2 text-primary">• {it.voucher_code}</span>}
                            </p>
                            <p className="text-xs text-warmgrey-muted mt-2">{new Date(it.created_at).toLocaleString()}</p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-headline">{it.title || it.name || it.question || it.label || it.slug}</p>
                            <p className="text-sm text-warmgrey line-clamp-1">{it.excerpt || it.quote || it.answer || it.tagline || ""}</p>
                          </>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {!isReadOnly && (
                          <>
                            <button onClick={() => setEditing(it.id)} className="text-sm text-primary hover:underline" data-testid={`admin-edit-${it.id}`}>Edit</button>
                            <button onClick={() => del(it.id)} className="text-sm text-warmgrey-muted hover:text-red-600" data-testid={`admin-delete-${it.id}`}><Trash2 size={14} /></button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
}
